import { Formik, Form } from 'formik';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Grid, Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import { snackAlertData } from 'utils/constants';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { updateAccount } from 'store/services/bank.Service';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import { Modal, TextField, Typography, SnackBarAlert } from 'shared';
import { bankFormSchema, bankFormInitialValues } from '../utils/bankFormSchema';

const BankForm = ({ type, open, initialData, handleCloseModal, handleEditBankSuccess }) => {
    const companyData = useSelector((state) => state.company.data);

    const [updateLoading, setUpdateLoading] = useState(false);
    const [alertData, setAlertData] = useState(snackAlertData);

    const handleSubmit = async (values, { setFieldError }) => {
        const { accountName, maskNumber } = values;
        try {
            setUpdateLoading(true);
            const data = {
                accountName,
                mask: maskNumber,
                id: initialData?.id,
                company_id: companyData.id,
            };
            const response = await updateAccount(data);

            handleSuccessAlert(setAlertData, response?.message);
            handleEditBankSuccess(response?.message);
            handleCloseModal();
        } catch (error) {
            const resp = error.response;

            if (resp?.status === 400 && resp.data && typeof resp.data === 'object') {
                Object.entries(resp.data).forEach(([field, messages]) => {
                    setFieldError(field, messages[0]);
                });
            } else {
                handleErrorAlert(setAlertData, resp?.data?.message ?? 'Something went wrong…');
            }
            setUpdateLoading(false);
        }
    };

    return (
        <Modal
            fullWidth
            open={open}
            title={`${type} Bank Account`}
            actionBtnTitle={<Typography text={type === 'Add' ? 'Create' : 'Update'} />}
            handleClose={handleCloseModal}
        >
            <Formik
                initialValues={bankFormInitialValues(initialData)}
                validationSchema={bankFormSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                        <Box width="100%" gap={2} mt={1} border="none">
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="accountName"
                                        placeholder="Account Name"
                                        value={values.accountName}
                                        onChange={(e) =>
                                            setFieldValue('accountName', e.target.value)
                                        }
                                        error={touched.accountName && !!errors.accountName}
                                        helperText={touched.accountName && errors.accountName}
                                        disabled={values.connectToQB}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="maskNumber"
                                        placeholder="Mask Number"
                                        value={values.maskNumber}
                                        onChange={(e) =>
                                            setFieldValue('maskNumber', e.target.value)
                                        }
                                        error={touched.maskNumber && !!errors.maskNumber}
                                        helperText={touched.maskNumber && errors.maskNumber}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        <Box display="flex" justifyContent="flex-end" marginTop={3} gap={2}>
                            <Button onClick={handleCloseModal}>Cancel</Button>
                            <LoadingButton
                                type="submit"
                                variant="contained"
                                color="primary"
                                loading={updateLoading}
                            >
                                {type === 'Add' ? 'Create' : 'Update'}
                            </LoadingButton>
                        </Box>
                    </Form>
                )}
            </Formik>

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Modal>
    );
};

export default BankForm;
